import { z } from 'zod';

// ============================================================================
// Task #3698 — DTO risposta condivisi per gli endpoint di instradamento
// chiamate consumati da packages/frontend/src/pages/CallRouting.tsx.
//
// Shape OSSERVATE dal dato reale in dev (curl autenticato 2026-08-12 su
// /routing/inbound|outbound|time-conditions|templates/* dopo create di
// fixture) e verificate contro packages/backend/src/services/telephony/routing.service.ts
// (getInboundRoutes/getOutboundRoutes/mapDbToTimeCondition/testDIDPattern/
// testOutboundPattern) e routing.routes.ts.
//
// Convenzioni wire (come schemas/crm-workflows.ts):
// - colonne NULLABLE / alias LEFT JOIN → .nullable()
// - timestamp → z.union([z.string(), z.date()])
// - jsonb genuinamente dinamici (destination_data, settings) → tipi larghi
//   documentati, NON any
// - SELECT * → colonne osservate + .passthrough()
// ============================================================================

const wireTimestamp = z.union([z.string(), z.date()]);

// ----------------------------------------------------------------------------
// destination_data e affini (jsonb libero). Shape osservate sul wire:
//   {}                                — default
//   { value: "<numero esterno>" }     — destinazione non-UUID (external)
//   { ai_error_fallback: {type,destination} } — fallback errore agente AI
// Il modello è genuinamente dinamico: record largo con chiavi note opzionali.
// ----------------------------------------------------------------------------
export const routingAiErrorFallbackSchema = z.object({
  type: z.string(), // extension|voicemail|queue|ivr|ring_group|hangup
  destination: z.string(),
});
export type RoutingAiErrorFallback = z.infer<typeof routingAiErrorFallbackSchema>;

export const routingDestinationDataSchema = z
  .object({
    value: z.string().optional(), // numero esterno per destinazioni non-UUID
    ai_error_fallback: routingAiErrorFallbackSchema.optional(),
  })
  .passthrough();
export type RoutingDestinationData = z.infer<typeof routingDestinationDataSchema>;

// ----------------------------------------------------------------------------
// GET /routing/inbound → data: InboundRouteDto[] (SELECT * FROM inbound_routes
// + alias destination_value=destination_id). GET/POST/PUT /routing/inbound/:id
// ritornano la stessa riga (senza alias destination_value sul detail raw).
// ----------------------------------------------------------------------------
export const inboundRouteDtoSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    store_id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
    did_number: z.string().nullable().optional(),
    did_e164: z.string().nullable().optional(), // canonica E.164 (source of truth match)
    caller_id_pattern: z.string().nullable().optional(),
    trunk_id: z.string().nullable().optional(),
    destination_type: z.string(),
    destination_id: z.string().nullable().optional(),
    // Alias FE-compat di destination_id aggiunto da getInboundRoutes (lista).
    destination_value: z.string().nullable().optional(),
    destination_data: routingDestinationDataSchema.nullable().optional(),
    time_condition_id: z.string().nullable().optional(),
    match_destination_type: z.string().nullable().optional(),
    match_destination_id: z.string().nullable().optional(),
    match_destination_data: routingDestinationDataSchema.nullable().optional(),
    nomatch_destination_type: z.string().nullable().optional(),
    nomatch_destination_id: z.string().nullable().optional(),
    nomatch_destination_data: routingDestinationDataSchema.nullable().optional(),
    holiday_destination_type: z.string().nullable().optional(),
    holiday_destination_id: z.string().nullable().optional(),
    holiday_destination_data: routingDestinationDataSchema.nullable().optional(),
    failover_enabled: z.boolean().nullable().optional(),
    failover_destination_type: z.string().nullable().optional(),
    failover_destination_id: z.string().nullable().optional(),
    failover_destination_data: routingDestinationDataSchema.nullable().optional(),
    priority: z.number().nullable().optional(),
    enabled: z.boolean(),
    is_standard_evergreen: z.boolean().nullable().optional(),
    ai_managed: z.boolean().nullable().optional(),
    settings: z.record(z.unknown()).nullable().optional(), // jsonb libero
    original_destination_snapshot: z.unknown().nullable().optional(),
    created_at: wireTimestamp.optional(),
    updated_at: wireTimestamp.optional(),
  })
  .passthrough();
export type InboundRouteDto = z.infer<typeof inboundRouteDtoSchema>;

// ----------------------------------------------------------------------------
// Trunk associato a rotta outbound (outbound_route_trunks + join nomi).
// Da getOutboundRoutes (mapping esplicito) e GET /routing/outbound/:id.
// ----------------------------------------------------------------------------
export const outboundRouteTrunkDtoSchema = z
  .object({
    id: z.string().optional(),
    // route_id assente nei trunks del template outbound-default (prefill).
    route_id: z.string().optional(),
    trunk_id: z.string(),
    trunk_name: z.string().nullable().optional(), // LEFT JOIN sip_trunks
    gateway_name: z.string().nullable().optional(),
    priority: z.number(),
    access_code: z.string().nullable().optional(),
    time_condition_id: z.string().nullable().optional(),
    time_condition_name: z.string().nullable().optional(), // LEFT JOIN time_conditions
    enabled: z.boolean(),
  })
  .passthrough();
export type OutboundRouteTrunkDto = z.infer<typeof outboundRouteTrunkDtoSchema>;

// ----------------------------------------------------------------------------
// GET /routing/outbound → data: OutboundRouteDto[] (SELECT or2.* + group_name/
// group_color da LEFT JOIN extension_groups, alias dial_pattern=pattern,
// trunk_id legacy=trunk_priority[0], trunks[] da outbound_route_trunks).
// Su POST create la riga può avere trunks: null e mancare group_name/color.
// ----------------------------------------------------------------------------
export const outboundRouteDtoSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    store_id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
    pattern: z.string().nullable().optional(),
    dial_pattern: z.string().nullable().optional(), // alias FE-compat di pattern
    caller_id_prefix: z.string().nullable().optional(),
    caller_id_number: z.string().nullable().optional(),
    trunk_priority: z.array(z.string()).nullable().optional(), // legacy
    disabled_trunk_references: z.array(z.string()).optional(),
    trunk_id: z.string().nullable().optional(), // legacy: trunk_priority[0]
    least_cost_routing: z.boolean().nullable().optional(),
    time_condition_id: z.string().nullable().optional(),
    match_destination_type: z.string().nullable().optional(),
    match_destination_id: z.string().nullable().optional(),
    match_destination_data: routingDestinationDataSchema.nullable().optional(),
    nomatch_destination_type: z.string().nullable().optional(),
    nomatch_destination_id: z.string().nullable().optional(),
    nomatch_destination_data: routingDestinationDataSchema.nullable().optional(),
    holiday_destination_type: z.string().nullable().optional(),
    holiday_destination_id: z.string().nullable().optional(),
    holiday_destination_data: routingDestinationDataSchema.nullable().optional(),
    strip_digits: z.number().nullable().optional(),
    add_digits: z.string().nullable().optional(),
    group_id: z.string().nullable().optional(),
    group_name: z.string().nullable().optional(), // LEFT JOIN extension_groups
    group_color: z.string().nullable().optional(),
    priority: z.number().nullable().optional(),
    enabled: z.boolean(),
    is_standard_evergreen: z.boolean().nullable().optional(),
    settings: z.record(z.unknown()).nullable().optional(),
    trunks: z.array(outboundRouteTrunkDtoSchema).nullable().optional(),
    created_at: wireTimestamp.optional(),
    updated_at: wireTimestamp.optional(),
  })
  .passthrough();
export type OutboundRouteDto = z.infer<typeof outboundRouteDtoSchema>;

// ----------------------------------------------------------------------------
// Time conditions. Wire = output di mapDbToTimeCondition (routing.service.ts):
// business_hours = colonna time_groups (jsonb libero): shape moderna osservata
//   { monday: { enabled, start, end }, ... } (record per giorno) MA il backend
//   supporta anche il formato legacy array [{days:[0..6], start_time, end_time}]
//   (vedi publish dialplan: Array.isArray(tc.business_hours)).
// ----------------------------------------------------------------------------
export const timeConditionDayScheduleSchema = z
  .object({
    enabled: z.boolean().optional(),
    start: z.string().optional(), // "09:00"
    end: z.string().optional(), // "18:00"
  })
  .passthrough();
export type TimeConditionDaySchedule = z.infer<typeof timeConditionDayScheduleSchema>;

export const timeConditionLegacyTimeGroupSchema = z
  .object({
    days: z.array(z.number()).optional(), // 0=Dom..6=Sab
    start_time: z.string().optional(),
    end_time: z.string().optional(),
  })
  .passthrough();
export type TimeConditionLegacyTimeGroup = z.infer<typeof timeConditionLegacyTimeGroupSchema>;

export const timeConditionBusinessHoursSchema = z.union([
  z.record(timeConditionDayScheduleSchema), // shape moderna per-giorno
  z.array(timeConditionLegacyTimeGroupSchema), // shape legacy time_groups
]);
export type TimeConditionBusinessHours = z.infer<typeof timeConditionBusinessHoursSchema>;

// Azioni valide lato backend (validActions in createTimeCondition).
export const timeConditionActionSchema = z.enum([
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'voicemail',
  'external',
  'hangup',
  'continue',
  'internal_ai_agent',
]);
export type TimeConditionAction = z.infer<typeof timeConditionActionSchema>;

// holidays: items del wrapper jsonb {items,action,destination}; ogni item è
// scritto dalla UI come {name,date,type} ma il jsonb è libero → campi noti
// opzionali + passthrough.
export const timeConditionHolidaySchema = z
  .object({
    name: z.string().optional(),
    date: z.string().optional(),
    type: z.string().optional(),
  })
  .passthrough();
export type TimeConditionHoliday = z.infer<typeof timeConditionHolidaySchema>;

export const timeConditionDtoSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    store_id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
    timezone: z.string(),
    business_hours: timeConditionBusinessHoursSchema,
    holidays: z.array(timeConditionHolidaySchema),
    business_hours_action: timeConditionActionSchema,
    business_hours_destination: z.string().optional(),
    business_hours_ai_error_fallback: routingAiErrorFallbackSchema.optional(),
    after_hours_action: timeConditionActionSchema,
    after_hours_destination: z.string().optional(),
    after_hours_ai_error_fallback: routingAiErrorFallbackSchema.optional(),
    holiday_calendar_id: z.string().nullable().optional(),
    holiday_action: timeConditionActionSchema,
    holiday_destination: z.string().optional(),
    holiday_ai_error_fallback: routingAiErrorFallbackSchema.optional(),
    enabled: z.boolean(),
  })
  .passthrough();
export type TimeConditionDto = z.infer<typeof timeConditionDtoSchema>;

// Write contracts deliberately close only the column boundary. Extensible
// destination/settings/schedule JSON retains the response contract's semantics.
// tenant_id is an ignored legacy echo, never the authority for a write.
const legacyTenantEcho = { tenant_id: z.string().optional() };
const routingInboundMutableSchema = inboundRouteDtoSchema.omit({
  id: true, tenant_id: true, created_at: true, updated_at: true,
  is_standard_evergreen: true, ai_managed: true,
  original_destination_snapshot: true, did_e164: true,
}).extend({
  name: z.string().min(1).max(100),
  destination_type: z.string().min(1).max(50),
  priority: z.number().int().nullable().optional(),
}).strict();

export const routingTrunkWriteSchema = outboundRouteTrunkDtoSchema.pick({
  trunk_id: true, priority: true, access_code: true,
  time_condition_id: true, enabled: true,
}).extend({
  trunk_id: z.string().min(1),
  priority: z.number().int().min(1).max(5),
  access_code: z.string().max(10).nullable().optional(),
  enabled: z.boolean().optional(),
}).strict();
export type RoutingTrunkWrite = z.infer<typeof routingTrunkWriteSchema>;

const routingOutboundMutableSchema = outboundRouteDtoSchema.omit({
  id: true, tenant_id: true, created_at: true, updated_at: true,
  is_standard_evergreen: true, group_name: true, group_color: true,
  disabled_trunk_references: true,
}).extend({
  name: z.string().min(1).max(100),
  pattern: z.string().max(200).optional(),
  dial_pattern: z.string().max(200).optional(),
  priority: z.number().int().nullable().optional(),
  strip_digits: z.number().int().nullable().optional(),
  trunks: z.array(routingTrunkWriteSchema).max(5).optional(),
}).strict();

export const routingInboundCreateSchema = routingInboundMutableSchema
  .partial().required({ name: true, destination_type: true })
  .extend(legacyTenantEcho)
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingInboundCreate = z.infer<typeof routingInboundCreateSchema>;
export const routingInboundPatchSchema = routingInboundMutableSchema.partial()
  .extend(legacyTenantEcho)
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingInboundPatch = z.infer<typeof routingInboundPatchSchema>;

export const routingOutboundCreateSchema = routingOutboundMutableSchema.partial()
  .required({ name: true }).extend(legacyTenantEcho)
  .refine(input => input.pattern !== undefined || input.dial_pattern !== undefined, {
    message: 'pattern or dial_pattern is required', path: ['pattern'],
  })
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingOutboundCreate = z.infer<typeof routingOutboundCreateSchema>;
export const routingOutboundPatchSchema = routingOutboundMutableSchema.partial()
  .extend(legacyTenantEcho)
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingOutboundPatch = z.infer<typeof routingOutboundPatchSchema>;

const routingTimeConditionMutableSchema = timeConditionDtoSchema.omit({
  id: true, tenant_id: true,
}).extend({
  name: z.string().min(1).max(100),
  condition_operator: z.enum(['AND', 'OR']).optional(),
  business_hours_destination: z.string().nullable().optional(),
  after_hours_destination: z.string().nullable().optional(),
  holiday_destination: z.string().nullable().optional(),
  business_hours_ai_error_fallback: routingAiErrorFallbackSchema.nullable().optional(),
  after_hours_ai_error_fallback: routingAiErrorFallbackSchema.nullable().optional(),
  holiday_ai_error_fallback: routingAiErrorFallbackSchema.nullable().optional(),
}).strict();
export const routingTimeConditionCreateSchema = routingTimeConditionMutableSchema.partial()
  .required({ name: true }).extend(legacyTenantEcho)
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingTimeConditionCreate = z.infer<typeof routingTimeConditionCreateSchema>;
export const routingTimeConditionPatchSchema = routingTimeConditionMutableSchema.partial()
  .extend(legacyTenantEcho)
  .transform(({ tenant_id: _tenant, ...input }) => input);
export type RoutingTimeConditionPatch = z.infer<typeof routingTimeConditionPatchSchema>;

// GET /routing/time-conditions → data: { time_conditions, total }
export const timeConditionsListDtoSchema = z.object({
  time_conditions: z.array(timeConditionDtoSchema),
  total: z.number(),
});
export type TimeConditionsListDto = z.infer<typeof timeConditionsListDtoSchema>;

// ----------------------------------------------------------------------------
// POST /routing/test-did-pattern e /routing/test-outbound-pattern
// (testDIDPattern/testOutboundPattern in routing.service.ts).
// ----------------------------------------------------------------------------
export const inboundPatternTestDtoSchema = z.object({
  matches: z.array(
    z.object({
      route: inboundRouteDtoSchema,
      matchType: z.enum(['exact', 'pattern', 'wildcard']),
      matchDetails: z.string(),
    })
  ),
  totalRoutes: z.number(),
});
export type InboundPatternTestDto = z.infer<typeof inboundPatternTestDtoSchema>;

export const outboundPatternIssueDtoSchema = z.object({
  routeId: z.string(),
  routeName: z.string(),
  pattern: z.string(),
  code: z.enum(['INVALID_PATTERN', 'UNSUPPORTED_PATTERN']),
  message: z.string(),
});
export type OutboundPatternIssueDto = z.infer<typeof outboundPatternIssueDtoSchema>;

export const outboundPatternTestDtoSchema = z.object({
  matches: z.array(
    z.object({
      route: outboundRouteDtoSchema,
      matchType: z.enum(['exact', 'pattern']),
      matchDetails: z.string(),
      transformedNumber: z.string().optional(),
    })
  ),
  totalRoutes: z.number(),
  issues: z.array(outboundPatternIssueDtoSchema).optional(),
});
export type OutboundPatternTestDto = z.infer<typeof outboundPatternTestDtoSchema>;

// ----------------------------------------------------------------------------
// GET /routing/templates/inbound-default e outbound-default → prefill form.
// Osservati dal wire: campi stringa con default '' (mai null), source =
// provenienza template.
// ----------------------------------------------------------------------------
export const inboundRouteTemplateDtoSchema = z
  .object({
    source: z.string(), // 'json_template' | altre provenienze
    source_name: z.string().optional(),
    name: z.string(),
    did_number: z.string(),
    caller_id_pattern: z.string(),
    trunk_id: z.string(),
    destination_type: z.string(),
    destination_id: z.string(),
    destination_data: routingDestinationDataSchema,
    time_condition_id: z.string(),
    match_destination_type: z.string(),
    match_destination_id: z.string(),
    nomatch_destination_type: z.string(),
    nomatch_destination_id: z.string(),
    holiday_destination_type: z.string(),
    holiday_destination_id: z.string(),
    failover_enabled: z.boolean(),
    failover_destination_type: z.string(),
    failover_destination_id: z.string(),
    failover_destination_data: routingDestinationDataSchema,
    priority: z.number(),
    enabled: z.boolean(),
    is_standard_evergreen: z.boolean(),
  })
  .passthrough();
export type InboundRouteTemplateDto = z.infer<typeof inboundRouteTemplateDtoSchema>;

export const outboundRouteTemplateDtoSchema = z
  .object({
    source: z.string(),
    source_name: z.string().optional(),
    name: z.string(),
    pattern: z.string(),
    dial_pattern: z.string(),
    caller_id_prefix: z.string(),
    caller_id_number: z.string(),
    least_cost_routing: z.boolean(),
    strip_digits: z.number(),
    add_digits: z.string(),
    time_condition_id: z.string(),
    match_destination_type: z.string(),
    match_destination_id: z.string(),
    nomatch_destination_type: z.string(),
    nomatch_destination_id: z.string(),
    holiday_destination_type: z.string(),
    holiday_destination_id: z.string(),
    group_id: z.string(),
    priority: z.number(),
    enabled: z.boolean(),
    is_standard_evergreen: z.boolean(),
    trunks: z.array(outboundRouteTrunkDtoSchema),
  })
  .passthrough();
export type OutboundRouteTemplateDto = z.infer<typeof outboundRouteTemplateDtoSchema>;

// ----------------------------------------------------------------------------
// Liste risorse usate dai select della pagina routing. Gli endpoint ritornano
// righe complete (SELECT * dei rispettivi service); qui modelliamo i campi
// effettivamente consumati dalla UI + .passthrough() per il resto.
// ----------------------------------------------------------------------------

// GET /sip-trunks → data: array (righe mascherate da /sip-trunks).
export const routingTrunkOptionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: z.string().nullable().optional(),
    // did_config: jsonb con main_number + did_pool (alias legacy inbound_dids).
    did_config: z
      .object({
        main_number: z.unknown().optional(),
        did_pool: z.unknown().optional(),
        inbound_dids: z.unknown().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();
export type RoutingTrunkOption = z.infer<typeof routingTrunkOptionSchema>;

// GET /voip/sip-extensions → data: { items, pagination } (paginatedResponse).
export const routingExtensionOptionSchema = z
  .object({
    id: z.string(),
    extension: z.string(),
    display_name: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
  })
  .passthrough();
export type RoutingExtensionOption = z.infer<typeof routingExtensionOptionSchema>;

export const routingExtensionListDtoSchema = z.object({
  items: z.array(routingExtensionOptionSchema),
  pagination: z
    .object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    })
    .passthrough()
    .optional(),
});
export type RoutingExtensionListDto = z.infer<typeof routingExtensionListDtoSchema>;

// GET /voip/ivr-menus | /voip/queues | /voip/ring-groups | /extension-groups
// → data: array di righe con id+name (+extension dove applicabile).
export const routingNamedOptionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    extension: z.string().nullable().optional(),
  })
  .passthrough();
export type RoutingNamedOption = z.infer<typeof routingNamedOptionSchema>;

// GET /voip/voicemail-boxes → data: array; identificatore = mailbox.
export const routingVoicemailOptionSchema = z
  .object({
    id: z.string(),
    mailbox: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
  })
  .passthrough();
export type RoutingVoicemailOption = z.infer<typeof routingVoicemailOptionSchema>;

// GET /ai/voice-agents → data: array (union OpenAI/ElevenLabs/Gemini rows con
// agent_platform/provider alias sintetici).
export const routingVoiceAgentOptionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    is_active: z.boolean().nullable().optional(),
    agent_platform: z.string().nullable().optional(), // 'openai'|'elevenlabs'|'gemini'|…
    provider: z.string().nullable().optional(),
    sip_enabled: z.boolean().nullable().optional(),
  })
  .passthrough();
export type RoutingVoiceAgentOption = z.infer<typeof routingVoiceAgentOptionSchema>;
